import { Controller, FieldError, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import { Grid } from "@mui/material";
import { LoadingButton } from "@mui/lab";

import PlanFormStyle from "./style";
import Validation from "./validation";
import FormFeatures from "./features";

import { EDuration, MultiLang } from "@/api/types/plans";
import FormText from "@/components/kit/FormGenerator/FormText";
import FormTextarea from "@/components/kit/FormGenerator/FormTextarea";
import FormCheckbox from "@/components/kit/FormGenerator/FormCheckbox";
import FormSelect from "@/components/kit/FormGenerator/FormSelect";
import { usePlanById } from "@/api/hooks/usePlans";
import { AvailableCurrency } from "@/api/types";
import { PlansDuration } from "@/constants/plansDuration";
// import { useGetCountries } from "@/api/hooks/useCountries";

export interface ICreatePlanForm {
    en_title: string;
    ar_title: string;
    en_name: string;
    ar_name: string;
    en_subtitle: string;
    ar_subtitle: string;
    en_description?: string;
    ar_description?: string;
    en_trialTitle?: string;
    ar_trialTitle?: string;
    en_trialSubtitle?: string;
    ar_trialSubtitle?: string;
    features: { en: string, ar: string, icon: string }[];
    currency: AvailableCurrency;
    isActive: boolean;
    price: number;
    duration: string;
    trialDuration?: string;
    googleProductId?: string;
    appleProductId?: string;
    paddleProductId?: string;
    paddleTrialProductId?: string;
    hasTrial?: boolean;
    showInApp?: boolean;
    showInWeb?: boolean;
}

const defaultValues: ICreatePlanForm = {
    en_title: "",
    ar_title: "",
    en_name: "",
    ar_name: "",
    en_subtitle: "",
    ar_subtitle: "",
    en_description: undefined,
    ar_description: undefined,
    en_trialTitle: undefined,
    ar_trialTitle: undefined,
    en_trialSubtitle: undefined,
    ar_trialSubtitle: undefined,
    features: [],
    showInApp: false,
    showInWeb: true,
    isActive: true,
    hasTrial: false,
    googleProductId: "",
    appleProductId: "",
    paddleProductId: "",
    paddleTrialProductId: "",
    currency: "USD",
    price: 0,
    duration: "1M",
    trialDuration: "3D",
};

interface IPlanForm {
    planId?: number;
    // eslint-disable-next-line no-unused-vars
    onSubmit: (fromData: ICreatePlanForm) => void;
}

export default function PlanForm({ onSubmit, planId }: IPlanForm) {
    const { data, isSuccess } = usePlanById({ id: Number(planId) });
    // const { countries } = useGetCountries();

    const content = data?.data;

    const {
        control,
        reset,
        watch,
        setValue,
        handleSubmit,
        trigger,
        formState: { errors, isValid },
    } = useForm<ICreatePlanForm>({
        defaultValues,
        mode: "all",
        resolver: yupResolver(Validation),
    });

    const isShowInApp = watch("showInApp");
    const isShowInWeb = watch("showInWeb");
    const hasTrial = watch("hasTrial");

    useEffect(() => {
        if (!planId) reset(defaultValues);
    }, [reset, planId]);

    useEffect(() => {
        // Trigger validation for trial fields when hasTrial changes
        trigger(["en_trialTitle", "ar_trialTitle", "en_trialSubtitle", "ar_trialSubtitle"]);
    }, [hasTrial, trigger]);

    useEffect(() => {
        if (content && isSuccess) {
            const {
                price,
                duration,
                trialDuration,
                googleProductId,
                appleProductId,
                paddleProductId,
                paddleTrialProductId,
                currency,
                hasTrial,
                isActive,
                showInApp,
                showInWeb,
                ml_features,
                ml_description,
                ml_title,
                ml_name,
                ml_subtitle,
                ml_trialTitle,
                ml_trialSubtitle,
            } = content;

            const { ar: ar_title, en: en_title } = ml_title;
            const { ar: ar_name, en: en_name } = ml_name || { ar: "", en: "" };
            const { ar: ar_description, en: en_description } = ml_description;
            const { ar: ar_subtitle, en: en_subtitle } = ml_subtitle || { ar: "", en: "" };
            const { ar: ar_trialTitle, en: en_trialTitle } = ml_trialTitle || { ar: "", en: "" };
            const { ar: ar_trialSubtitle, en: en_trialSubtitle } = ml_trialSubtitle || { ar: "", en: "" };

            // Convert ml_features to form structure
            const formFeatures =
                ml_features?.map((feature) => ({
                    en: feature.en,
                    ar: feature.ar,
                    icon: feature.icon || "",
                })) || [];

            reset({
                en_title,
                ar_title,
                en_name,
                ar_name,
                en_subtitle,
                ar_subtitle,
                en_description,
                ar_description,
                en_trialTitle,
                ar_trialTitle,
                en_trialSubtitle,
                ar_trialSubtitle,
                features: formFeatures,
                showInApp,
                showInWeb,
                isActive,
                hasTrial,
                googleProductId,
                appleProductId,
                paddleProductId,
                paddleTrialProductId,
                currency,
                price,
                duration,
                trialDuration,
            });
        }
    }, [reset, isSuccess, content]);

    // const Currencies = countries.map((item) => ({ value: item.currencyIso, label: item.currencyIso }));
    const Currencies = [{ value: "USD", label: "USD" }];

    return (
        <PlanFormStyle className="form-style">
            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={1}>
                    <Grid item xs={6}>
                        <Controller
                            key="en_name"
                            control={control}
                            name="en_name"
                            render={({ field }) => (
                                <FormText
                                    errorMessage={errors["en_name"] as FieldError}
                                    field={{ ...field }}
                                    label="English Name"
                                    placeholder="English Name"
                                    props={{ required: true }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Controller
                            key="ar_name"
                            control={control}
                            name="ar_name"
                            render={({ field }) => (
                                <FormText
                                    errorMessage={errors["ar_name"] as FieldError}
                                    field={{ ...field }}
                                    label="Arabic Name"
                                    placeholder="Arabic Name"
                                    props={{
                                        required: true,
                                        sx: {
                                            direction: "rtl",
                                        },
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Controller
                            key="en_title"
                            control={control}
                            name="en_title"
                            render={({ field }) => (
                                <FormText
                                    errorMessage={errors["en_title"] as FieldError}
                                    field={{ ...field }}
                                    label="English Title"
                                    placeholder="English Title"
                                    props={{ required: true }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Controller
                            key="ar_title"
                            control={control}
                            name="ar_title"
                            render={({ field }) => (
                                <FormText
                                    errorMessage={errors["ar_title"] as FieldError}
                                    field={{ ...field }}
                                    label="Arabic Title"
                                    placeholder="Arabic title"
                                    props={{
                                        required: true,
                                        sx: {
                                            direction: "rtl",
                                        },
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <Controller
                            key="en_subtitle"
                            control={control}
                            name="en_subtitle"
                            render={({ field }) => (
                                <FormText
                                    errorMessage={errors["en_subtitle"] as FieldError}
                                    field={{ ...field }}
                                    label="English Subtitle"
                                    placeholder="English Subtitle"
                                    props={{ required: true }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Controller
                            key="ar_subtitle"
                            control={control}
                            name="ar_subtitle"
                            render={({ field }) => (
                                <FormText
                                    errorMessage={errors["ar_subtitle"] as FieldError}
                                    field={{ ...field }}
                                    label="Arabic Subtitle"
                                    placeholder="Arabic Subtitle"
                                    props={{
                                        required: true,
                                        sx: {
                                            direction: "rtl",
                                        },
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Controller
                            key="en_trialTitle"
                            control={control}
                            name="en_trialTitle"
                            render={({ field }) => (
                                <FormText
                                    errorMessage={errors["en_trialTitle"] as FieldError}
                                    field={{ ...field }}
                                    label="English Trial Title"
                                    placeholder="English Trial Title"
                                    props={{ required: hasTrial }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Controller
                            key="ar_trialTitle"
                            control={control}
                            name="ar_trialTitle"
                            render={({ field }) => (
                                <FormText
                                    errorMessage={errors["ar_trialTitle"] as FieldError}
                                    field={{ ...field }}
                                    label="Arabic Trial Title"
                                    placeholder="Arabic Trial Title"
                                    props={{
                                        required: hasTrial,
                                        sx: {
                                            direction: "rtl",
                                        },
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Controller
                            key="en_trialSubtitle"
                            control={control}
                            name="en_trialSubtitle"
                            render={({ field }) => (
                                <FormText
                                    errorMessage={errors["en_trialSubtitle"] as FieldError}
                                    field={{ ...field }}
                                    label="English Trial Subtitle"
                                    placeholder="English Trial Subtitle"
                                    props={{ required: hasTrial }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Controller
                            key="ar_trialSubtitle"
                            control={control}
                            name="ar_trialSubtitle"
                            render={({ field }) => (
                                <FormText
                                    errorMessage={errors["ar_trialSubtitle"] as FieldError}
                                    field={{ ...field }}
                                    label="Arabic Trial Subtitle"
                                    placeholder="Arabic Trial Subtitle"
                                    props={{
                                        required: hasTrial,
                                        sx: {
                                            direction: "rtl",
                                        },
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Controller
                            key="en_description"
                            control={control}
                            name="en_description"
                            render={({ field }) => (
                                <FormTextarea
                                    className="textarea"
                                    errorMessage={errors["en_description"] as FieldError}
                                    field={{ ...field }}
                                    label="English Description"
                                    placeholder="English Description"
                                    props={{ required: false }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Controller
                            key="ar_description"
                            control={control}
                            name="ar_description"
                            render={({ field }) => (
                                <FormTextarea
                                    className="textarea"
                                    errorMessage={errors["ar_description"] as FieldError}
                                    field={{ ...field }}
                                    label="Arabic Description"
                                    placeholder="Arabic Description"
                                    props={{
                                        required: false,
                                        sx: {
                                            direction: "rtl",
                                        },
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Controller
                            key="features"
                            control={control}
                            name="features"
                            render={({ field }) => {
                                return (
                                    <FormFeatures
                                        className="input-filed features"
                                        errorMessage={(errors["features"]?.message || "") as string}
                                        field={{ ...field }}
                                    />
                                );
                            }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Controller
                            key="currency"
                            control={control}
                            name="currency"
                            render={({ field }) => (
                                <FormSelect
                                    className="input-filed"
                                    data={Currencies}
                                    errorMessage={errors["currency"] as FieldError}
                                    field={{ ...field }}
                                    label="Currency"
                                    placeholder="Select Items"
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Controller
                            key="price"
                            control={control}
                            name="price"
                            render={({ field }) => (
                                <FormText
                                    className="input-filed"
                                    errorMessage={errors["price"] as FieldError}
                                    field={{ ...field }}
                                    label="Price"
                                    placeholder="Price"
                                    props={{ required: true }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <Controller
                            key="googleProductId"
                            control={control}
                            name="googleProductId"
                            render={({ field }) => (
                                <FormText
                                    className="input-filed"
                                    errorMessage={errors["googleProductId"] as FieldError}
                                    field={{ ...field }}
                                    label="Google Product Id"
                                    placeholder="Google Product Id"
                                    props={{ required: isShowInApp }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Controller
                            key="appleProductId"
                            control={control}
                            name="appleProductId"
                            render={({ field }) => (
                                <FormText
                                    className="input-filed"
                                    errorMessage={errors["appleProductId"] as FieldError}
                                    field={{ ...field }}
                                    label="Apple Product Id"
                                    placeholder="Apple Product Id"
                                    props={{ required: isShowInApp }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Controller
                            key="paddleProductId"
                            control={control}
                            name="paddleProductId"
                            render={({ field }) => (
                                <FormText
                                    className="input-filed"
                                    errorMessage={errors["paddleProductId"] as FieldError}
                                    field={{ ...field }}
                                    label="Paddle Product Id"
                                    placeholder="Paddle Product Id"
                                    props={{ required: isShowInWeb }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Controller
                            key="paddleTrialProductId"
                            control={control}
                            name="paddleTrialProductId"
                            render={({ field }) => (
                                <FormText
                                    className="input-filed"
                                    errorMessage={errors["paddleTrialProductId"] as FieldError}
                                    field={{ ...field }}
                                    label="Paddle Trial Product Id"
                                    placeholder="Paddle Trial Product Id"
                                    props={{ required: isShowInWeb && hasTrial }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <Controller
                            key="duration"
                            control={control}
                            name="duration"
                            render={({ field }) => (
                                <FormSelect
                                    className="input-filed"
                                    data={PlansDuration.map((item) => ({ label: item, value: item }))}
                                    errorMessage={errors["duration"] as FieldError}
                                    field={{ ...field, onChange: (e) => setValue("duration", e.target.value) }}
                                    label="Plans Period"
                                    placeholder="Plans period duration"
                                    props={{ required: true }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Controller
                            key="trialDuration"
                            control={control}
                            name="trialDuration"
                            render={({ field }) => (
                                <FormSelect
                                    className="input-filed"
                                    data={PlansDuration.map((item) => ({ label: item, value: item }))}
                                    errorMessage={errors["trialDuration"] as FieldError}
                                    field={{ ...field, onChange: (e) => setValue("trialDuration", e.target.value) }}
                                    label="Trial Period"
                                    placeholder="Trial period duration"
                                    props={{ required: hasTrial }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container>
                            <Grid item xs={3}>
                                <Controller
                                    key="showInApp"
                                    control={control}
                                    name="showInApp"
                                    render={({ field }) => (
                                        <FormCheckbox
                                            className="input-filed"
                                            errorMessage={errors["showInApp"] as FieldError}
                                            field={{ ...field }}
                                            label="Show In App"
                                            placeholder="Show In App"
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <Controller
                                    key="showInWeb"
                                    control={control}
                                    name="showInWeb"
                                    render={({ field }) => (
                                        <FormCheckbox
                                            className="input-filed"
                                            errorMessage={errors["showInWeb"] as FieldError}
                                            field={{ ...field }}
                                            label="Show in Web"
                                            placeholder="Show in Web"
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <Controller
                                    key="hasTrial"
                                    control={control}
                                    name="hasTrial"
                                    render={({ field }) => (
                                        <FormCheckbox
                                            className="input-filed"
                                            errorMessage={errors["hasTrial"] as FieldError}
                                            field={{ ...field }}
                                            label="Has Trial"
                                            placeholder="Has Trial"
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <Controller
                                    key="isActive"
                                    control={control}
                                    name="isActive"
                                    render={({ field }) => (
                                        <FormCheckbox
                                            className="input-filed"
                                            errorMessage={errors["isActive"] as FieldError}
                                            field={{ ...field }}
                                            label="Is Active"
                                            placeholder="Is Active"
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <LoadingButton color="primary" disabled={!isValid} type="submit" variant="contained">
                            {planId ? "Save" : "Create"}
                        </LoadingButton>
                    </Grid>
                </Grid>
            </form>
        </PlanFormStyle>
    );
}
